/* SEO-friendly URL helpers for recipes & cookbooks.

   Public URLs now look like:
     /r/grandmas-chocolate-cake-rcp_18a5860a4fdd
     /cb/family-favourites-cb_059c7dbf792f

   The trailing token is always the canonical {recipe_id|cookbook_id} so we
   can extract it back unambiguously on the page. Old links like
     /r/rcp_18a5860a4fdd
   continue to work because `idFromSlug` falls back to returning the whole
   param when no hyphen is present. */

const STOPWORDS = new Set(["a", "an", "the", "and", "or", "of", "in", "on", "for", "to", "with", "by"]);

/** Convert any string into a URL-safe kebab-case slug. */
export function slugify(input) {
  if (!input || typeof input !== "string") return "";
  return (
    input
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/['']/g, "")
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .split("-")
      .filter((tok, idx, arr) => tok && !(arr.length > 4 && STOPWORDS.has(tok)))
      .slice(0, 8)
      .join("-")
  );
}

/** Extract the canonical recipe_id / cookbook_id from a slug-id route param. */
export function idFromSlug(param) {
  if (!param) return "";
  const idx = param.lastIndexOf("-");
  return idx >= 0 ? param.slice(idx + 1) : param;
}

/** Build the SEO path for a recipe. Tolerant of partial recipe objects.
    Accepts `{recipe_id, title}` OR just an id string (legacy callsites). */
export function recipePath(recipeOrId, title) {
  if (!recipeOrId) return "/";
  if (typeof recipeOrId === "string") {
    const slug = slugify(title || "");
    return slug ? `/r/${slug}-${recipeOrId}` : `/r/${recipeOrId}`;
  }
  const id = recipeOrId.recipe_id || recipeOrId.id;
  if (!id) return "/";
  const slug = slugify(recipeOrId.title || title || "");
  return slug ? `/r/${slug}-${id}` : `/r/${id}`;
}

/** Build the SEO path for a cookbook. Same calling conventions as recipePath. */
export function cookbookPath(cookbookOrId, title) {
  if (!cookbookOrId) return "/";
  if (typeof cookbookOrId === "string") {
    const slug = slugify(title || "");
    return slug ? `/cb/${slug}-${cookbookOrId}` : `/cb/${cookbookOrId}`;
  }
  const id = cookbookOrId.cookbook_id || cookbookOrId.id;
  const slug = slugify(cookbookOrId.title || title || "");
  return slug ? `/cb/${slug}-${id}` : `/cb/${id}`;
}
